'use client';

import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

interface SectionCardProps {
  title: string;
  /** Colored dot before the title */
  dotColor?: string;
  /** Override the title text color */
  titleColor?: string;
  /** Border color override (e.g. border-red-200) */
  borderColor?: string;
  children: ReactNode;
  className?: string;
}

export function SectionCard({
  title,
  dotColor,
  titleColor = 'text-gray-900',
  borderColor,
  children,
  className,
}: SectionCardProps) {
  return (
    <div className={cn('bg-white border rounded-xl p-6', borderColor || 'border-gray-200', className)}>
      <h3 className={cn('text-lg font-semibold mb-3 flex items-center gap-2', titleColor)}>
        {dotColor && <span className={cn('w-2 h-2 rounded-full shrink-0', dotColor)} />}
        {title}
      </h3>
      {children}
    </div>
  );
}

interface SectionParagraphProps {
  text: string | null | undefined;
  className?: string;
}

/** Renders a text block with newlines preserved */
export function SectionParagraph({ text, className }: SectionParagraphProps) {
  if (!text) return null;
  return <div className={cn('text-gray-700 leading-relaxed text-sm whitespace-pre-line', className)}>{text}</div>;
}

interface SectionListProps {
  items: string[] | null | undefined;
  className?: string;
}

/** Renders a bulleted list from a string array or a newline-delimited string */
export function SectionList({ items, className }: SectionListProps) {
  if (!items || items.length === 0) return null;
  return (
    <ul className={cn('space-y-1.5', className)}>
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Parses a string that may be a paragraph or a newline/dash-delimited list */
export function parseTextToList(text: string | null | undefined): string[] {
  if (!text) return [];
  const lines = text.split('\n').map(l => l.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
  return lines;
}
